import {
	Box,
	Card,
	Collapse,
	Typography,
	CardContent,
	Button,
	IconButton,
	CardActionArea,
	Tooltip,
	CircularProgress,
	Divider,
	Link,
} from "@mui/material";

import { ExpandMoreRounded } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent, {
	timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";

import format from "date-fns/format";

import { third_party_constants } from "constants/third_party_constants";

const SearchResultComponent = ({ item }) => {
	const token = useSelector((state) => state.auth.token);

	const [displayDetails, setDisplayDetails] = useState(false);
	const [isDisabled, setIsDisabled] = useState(true);
	const [details, setDetails] = useState([]);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);

	const getSearchQueryString = (item) => {
		console.log("item is", item);
		if (!item) return "no search query specified";
		switch (item.kind) {
			case "people":
				return item.email;
			case "message":
				return item.title;
			case "file":
				return item.name;
		}
	};

	useEffect(() => {
		setIsDisabled(item === null);
		setDisplayDetails(item !== null);

		{
			/* GET ITEM DETAILS */
		}
		const getDetails = async () => {
			if (item) {
				try {
					setIsLoadingDetails(true);
					setDetails([]);
					switch (item?.kind) {
						/* PEOPLE PAGE SEARCH */
						case "people":
							const responsePeople = await fetch(
								`${process.env.REACT_APP_PORT}/people/getUserInteractions/${item?.email}`,
								{
									method: "GET",
									headers: {
										"Content-Type": "application/json",
										Authorization: `Bearer ${token}`,
									},
								}
							);

							const data = await responsePeople.json();
							if (responsePeople.status === 200) setDetails(data.interactions);
							else setDetails([]);

							setIsLoadingDetails(false);
							break;

						/* MESSAGE SEARCH */
						case "message":
							setDetails([item]);
							setIsLoadingDetails(false);
							break;

						/* SEARCH PAGE SEARCH */
						case "file":
							const url = `${process.env.REACT_APP_PORT}/summarizer/getOpenAIResponse`;
							const body = {
								notes: [item],
							};

							const responseFile = await fetch(url, {
								method: "POST",
								headers: {
									"Content-Type": "application/json",
									Authorization: `Bearer ${token}`,
								},
								body: JSON.stringify(body),
							});
							const res = await responseFile.json();
							if (responseFile.status === 200) setDetails(res?.data);
							else setDetails([]);

							setIsLoadingDetails(false);
							break;
					}
				} catch (error) {
					setDetails([]);
					setIsLoadingDetails(false);
				}
			}
		};
		getDetails();
	}, [item]);

	const ExpandMore = styled((props) => {
		const { expand, ...other } = props;
		return <IconButton {...other} />;
	})(({ theme, expand }) => ({
		transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
		marginLeft: "auto",
		transition: theme.transitions.create("transform", {
			duration: theme.transitions.duration.shortest,
		}),
	}));

	return (
		<Card
			raised
			sx={{
				height: "300px",
				height: "100%",
				width: "100%",
				background: "white",
				borderRadius: "35px",
				mb: "15px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "5px",
				}}
			>
				<Typography
					variant="h3"
					sx={{ paddingLeft: "15px", paddingRight: "50px", color: "#3B57B8" }}
				>
					<span style={{ fontWeight: "bold" }}>Search Results for: </span>
					{getSearchQueryString(item)}
				</Typography>
				<ExpandMore
					expand={displayDetails}
					onClick={() => setDisplayDetails(!displayDetails)}
					aria-expanded={displayDetails}
					aria-label="Show Details"
					disabled={isDisabled}
				>
					<Tooltip
						title={
							!displayDetails ? "Show Search Details" : "Hide Search Details"
						}
					>
						<ExpandMoreRounded />
					</Tooltip>
				</ExpandMore>
			</Box>

			<Collapse in={displayDetails} timeout="auto" unmountOnExit>
				{item !== null && (
					<Box
						sx={{
							p: "0.5rem",
						}}
					>
						<Card
							raised
							sx={{ borderRadius: "35px", mx: "15px", my: "15px", p: "0.5rem" }}
						>
							<Box>
								{isLoadingDetails && (
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
										}}
									>
										<CircularProgress />
									</Box>
								)}
								{!isLoadingDetails && details?.length > 0 && (
									<Box>
										<Box display="flex" flexDirection="column">
											<Box
												sx={{
													borderColor: "#3B57B8",
													borderRadius: "15px",
													maxHeight: "200px",
													overflow: "auto",
												}}
											>
												{/* PEOPLE PAGE PREVIEW */}
												{item.kind === "people" && (
													<Box
														sx={{
															textAlign: "center",
														}}
													>
														<Typography
															variant="h4"
															sx={{ color: "#3B57B8", fontWeight: "500" }}
														>
															People Page preview for{" "}
															<Link
																href={`https://motefrontend.onrender.com/people/${item?.resource}`}
															>
																{item?.email}
															</Link>
														</Typography>
														<Divider sx={{ my: "1rem" }} />

														<Timeline
															sx={{
																[`& .${timelineOppositeContentClasses.root}`]: {
																	flex: 0.2,
																},
															}}
														>
															{details?.map((interaction, i) => (
																<TimelineItem key={interaction.id}>
																	<TimelineOppositeContent color="text.secondary">
																		{format(
																			new Date(interaction.date),
																			"MMM dd, yyyy"
																		)}
																	</TimelineOppositeContent>

																	<TimelineSeparator>
																		<TimelineDot color="background">
																			{interaction.type.startsWith("gmail")
																				? third_party_constants.gmail.icon()
																				: interaction.type === "gcal"
																				? third_party_constants.google_calendar.icon()
																				: interaction.type === "slack"
																				? third_party_constants.slack.icon()
																				: undefined}
																		</TimelineDot>
																		{i !== details.length - 1 && (
																			<TimelineConnector
																				sx={{ bgcolor: "primary.main" }}
																			/>
																		)}
																	</TimelineSeparator>

																	<TimelineContent>
																		<Typography variant="body"></Typography>
																		<Card>
																			<CardActionArea
																				disableTouchRipple
																				href={interaction.htmlLink}
																				target="_blank"
																			>
																				<CardContent>
																					<Typography
																						gutterBottom
																						variant="h5"
																						component="div"
																					>
																						{interaction.title}
																					</Typography>
																				</CardContent>
																			</CardActionArea>
																		</Card>
																	</TimelineContent>
																</TimelineItem>
															))}
														</Timeline>
													</Box>
												)}

												{/* MESSAGE PREVIEW */}
												{item.kind === "message" && (
													<Box>
														<Box sx={{ textAlign: "center" }}>
															<Typography
																variant="h4"
																sx={{ color: "#3B57B8", fontWeight: "500" }}
															>
																Message preview for {details[0]?.title}
															</Typography>
														</Box>
														<Divider sx={{ my: "1rem" }} />
														<Box sx={{ px: "1rem" }}>
															<Typography>
																{details[0]?.body ||
																	"No message body available."}
															</Typography>
														</Box>
													</Box>
												)}

												{/* SEARCH PAGE PREVIEW -- FILE */}
												{item.kind === "file" && (
													<Box>
														{details?.map((file) => (
															<>
																<Box sx={{ textAlign: "center" }}>
																	<Typography
																		variant="h4"
																		sx={{ color: "#3B57B8", fontWeight: "500" }}
																	>
																		File preview for {file?.fileName}
																	</Typography>
																</Box>
																<Divider sx={{ my: "1rem" }} />
																<Box sx={{ px: "1rem" }}>
																	<Typography>{file?.summary}</Typography>
																</Box>
															</>
														))}
													</Box>
												)}
											</Box>
										</Box>
									</Box>
								)}
							</Box>
						</Card>
					</Box>
				)}
			</Collapse>
		</Card>
	);
};

export default SearchResultComponent;
